import {
  inject,
  type InjectionKey,
  provide,
  reactive,
  ref,
  toRefs,
  watch,
} from 'vue'
import { ApolloCache, type Reference } from '@apollo/client/cache'
import { addListItemToCache, deleteListItemFromCache } from '/@/utils/apollo'
import {
  type ProjectBudgetStatus,
  type ProjectThingBudget,
  type ProjectThingBudgetStatus,
} from './types'
import { projectQuery } from '/@/common/projects/api/fragments'
import { type Project } from '/@/common/projects/types'
import { isProduct, isProjectGenericThing } from '/@/common/things/utils'
import { throwError } from '/@/core/errors'

export const BudgetFeatureHintSymbol: InjectionKey<string> = Symbol(
  'budget-feature-hint',
)

export function getProjectThingBudgetStatusText(
  status: ProjectThingBudgetStatus,
) {
  if (status === 'APPROVED') return `added`
  else if (status === 'COMPLETED') return `installed`
  else if (status === 'SOURCE') return `to source`
  else if (status === 'LATER') return `save for later`
  return status.toLowerCase()
}

export function getBudgetStatusColor(status?: ProjectBudgetStatus) {
  switch (status) {
    case 'DRAFT':
      return 'bg-yellow-50'
    case 'APPROVED':
      return 'bg-green-50'
    case 'REJECTED':
      return 'bg-red-50'
    case 'PENDING':
      return 'bg-purple-50'
    default:
      return null
  }
}

export function getBudgetSpendColor(progressPercentage: number) {
  if (progressPercentage >= 100) {
    return 'text-red-70'
  } else if (progressPercentage >= 75) {
    return 'text-yellow-70'
  } else {
    return null
  }
}

export function getBudgetProgressColor(progressPercentage: number) {
  if (progressPercentage >= 100) {
    return 'bg-red-70'
  } else if (progressPercentage >= 75) {
    return 'bg-yellow-50'
  } else if (progressPercentage > 0) {
    return 'bg-green-50'
  } else {
    return 'bg-navy-30'
  }
}

export function getBudgetProgressBackgroundColor(progressPercentage: number) {
  if (progressPercentage >= 100) {
    return 'bg-red-10'
  } else if (progressPercentage >= 75) {
    return 'bg-yellow-10'
  } else if (progressPercentage > 0) {
    return 'bg-green-10'
  } else {
    return 'bg-navy-10'
  }
}

interface Options {
  insertMode: 'prepend' | 'append'
}

export function useProjectThingBudgetCache() {
  function getCachedProjectThingBudget(
    cache: ApolloCache<unknown>,
    projectId: string,
    projectThingBudgetId: string,
  ) {
    const projectThing = cache
      .readQuery<{ project: Project }>({
        query: projectQuery,
        variables: {
          projectId,
        },
      })
      ?.project?.things.find(t => {
        return (
          (isProduct(t) || isProjectGenericThing(t)) &&
          t.thingBudget &&
          t.thingBudget.id === projectThingBudgetId
        )
      })
    return projectThing &&
      (isProduct(projectThing) || isProjectGenericThing(projectThing))
      ? projectThing.thingBudget
      : null
  }

  function deleteListItem(
    cache: ApolloCache<undefined>,
    fieldName: keyof ProjectThingBudget,
    itemId: string,
    projectId: string,
    projectThingBudgetId: string,
  ) {
    const projectThingBudget = getCachedProjectThingBudget(
      cache,
      projectId,
      projectThingBudgetId,
    )
    if (!projectThingBudget) return
    const id = cache.identify({ ...projectThingBudget })
    if (!id) return
    return deleteListItemFromCache<ProjectThingBudget>(
      id,
      cache,
      fieldName,
      itemId,
    )
  }

  function addListItem(
    cache: ApolloCache<undefined>,
    fieldName: keyof ProjectThingBudget,
    newItemRef: Reference,
    projectId: string,
    projectThingBudgetId: string,
    options: Options = { insertMode: 'append' },
  ) {
    const projectThingBudget = getCachedProjectThingBudget(
      cache,
      projectId,
      projectThingBudgetId,
    )
    if (!projectThingBudget) return
    const id = cache.identify({ ...projectThingBudget })
    if (!id) return
    return addListItemToCache<ProjectThingBudget>(
      id,
      cache,
      fieldName,
      newItemRef,
      options,
    )
  }

  return {
    addListItem,
    deleteListItem,
  }
}

export type BudgetGroupByType =
  | 'section'
  | 'brand'
  | 'category'
  | 'status'
  | 'none'
export type BudgetColumnType =
  | 'Item Name'
  | 'Brand'
  | 'Section'
  | 'Category'
  | 'Price'
  | 'Quantity'
  | 'Tax'
  | 'Shipping'
  | 'Markup'
  | 'Discount'
  | 'Total'
  | 'Status'
  | 'Order Number'
  | 'Tracking Info'
  | 'Lead Time'
  | 'Estimated Delivery'
  | 'Note'
  | 'Updated'

export const budgetColumnTypes: BudgetColumnType[] = [
  'Item Name',
  'Brand',
  'Section',
  'Category',
  'Price',
  'Quantity',
  'Tax',
  'Shipping',
  'Markup',
  'Discount',
  'Total',
  'Status',
  'Order Number',
  'Tracking Info',
  'Lead Time',
  'Estimated Delivery',
  'Note',
  'Updated',
]
const viewModeHiddenBudgetColumnTypes: BudgetColumnType[] = [
  'Price',
  'Tax',
  'Shipping',
  'Markup',
  'Discount',
  'Updated',
]
export const viewModeBudgetColumnTypes: BudgetColumnType[] =
  budgetColumnTypes.filter(p => !viewModeHiddenBudgetColumnTypes.includes(p))

interface ModifiersState {
  groupBy: BudgetGroupByType
  hiddenColumns: BudgetColumnType[]
}

function createDefaultModifiers() {
  const state: ModifiersState = {
    groupBy: 'section',
    hiddenColumns: [],
  }
  return state
}

function createBudgetModifiers(id: string | null) {
  // The main state is a reactive object
  const state = reactive<ModifiersState>(createDefaultModifiers())
  const isSetting = ref(false)

  function set(newState: ModifiersState) {
    isSetting.value = true

    state.groupBy = newState.groupBy
    state.hiddenColumns = newState.hiddenColumns

    isSetting.value = false
  }

  function save(stateToSave: ModifiersState) {
    if (!id || typeof sessionStorage === 'undefined') return
    sessionStorage.setItem(id, JSON.stringify(stateToSave))
  }

  function clear() {
    const defaults = createDefaultModifiers()
    set(defaults)
    save(state)
  }

  function restore() {
    if (!id || typeof sessionStorage === 'undefined') return
    try {
      const prevState = JSON.parse(sessionStorage.getItem(id) ?? '')
      if (prevState) {
        set(prevState)
      }
    } catch {
      //
    }
  }

  // a bit expensive, but that's it
  watch(
    () => state,
    modState => {
      if (isSetting.value) return

      // using rAF to avoid blocking the UI
      requestAnimationFrame(() => {
        save(modState)
      })
    },
    {
      deep: true,
    },
  )

  return reactive({
    ...toRefs(state),
    clear,
    restore,
  })
}

export type BudgetModifiers = ReturnType<typeof createBudgetModifiers>
export const BudgetModifiersSymbol: InjectionKey<BudgetModifiers> =
  Symbol('budget-modifiers')

export function installBudgetModifiers(projectId: string | null) {
  const modifiers = createBudgetModifiers(projectId)
  provide(BudgetModifiersSymbol, modifiers)
  return modifiers
}

export function useBudgetModifiers() {
  const modifiers = inject(BudgetModifiersSymbol)
  if (!modifiers) {
    throwError('Module budget-modifiers was not installed correctly.')
    return createBudgetModifiers('errored')
  }
  return modifiers
}
